import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LOGIN_WITH_HASH, TRIPS } from "../constants/ROUTER_PATH_TITLE";
import useTrip from "./useTrip";
import useUser from "./useUser";
import useAuth from "./useAuth";
import Model from "@tripian/model";

const useTripHashParams = (hashParam?: string) => {
  const { isLoggedIn } = useAuth();
  const {
    tripReferences,
    loadingTripReferences,
    userTripReferencesFetch,
    user,
    loadingUser,
  } = useUser();
  const { tripReference, tripFetch, tripGetShared } = useTrip();

  const { hash, hashLogin, shared } = useMemo(() => {
    if (!hashParam) return { hash: "", hashLogin: false, shared: false };

    const params = hashParam.split("!");
    const hash = params[0];
    const hashLogin = params[1] === "l";
    const shared = params[1] === "s";
    return { hash, hashLogin, shared };
  }, [hashParam]);

  const navigate = useNavigate();

  /**
   * Fetch for loggedIn user
   * My Trips
   */
  useEffect(() => {
    if (loadingUser) return;

    const isVisaFlow = window.tconfig?.ELIGIBILITY_TYPE === "visa";

    if (isLoggedIn && user) {
      if (!isVisaFlow) {
        // Visa flow değilse direkt request at
        if (tripReferences === undefined && !loadingTripReferences) {
          userTripReferencesFetch();
        }
      } else {
        // Visa flow ise userType kontrolü yap
        const needsVisaRedirect = user.userType !== Model.USER_TYPE.ELIGIBILITY;
        if (
          !needsVisaRedirect &&
          tripReferences === undefined &&
          !loadingTripReferences
        ) {
          userTripReferencesFetch();
        }
      }
    }
  }, [
    isLoggedIn,
    loadingTripReferences,
    tripReferences,
    userTripReferencesFetch,
    user,
    loadingUser,
  ]);

  /**
   * Fetch for loggedIn user (own trip), or redirect if hash doesn't belong to them and isn't shared
   * TripReference
   */
  useEffect(() => {
    if (isLoggedIn && tripReferences && tripReference === undefined) {
      if (tripReferences.some(x => x.tripHash === hash)) {
        // console.log("Fetch for loggedIn user (own trip)");
        tripFetch(hash);
      } else if (hash && !shared) {
        // Hash not in own trips and no shared flag → redirect instead of white screen
        navigate(TRIPS.PATH, { replace: true });
      }
    }
  }, [hash, isLoggedIn, tripFetch, tripReference, tripReferences, shared, navigate]);

  /**
   * Fetch for loggedIn user, get shared trip (not own trip)
   * TripReference
   */
  useEffect(() => {
    if (isLoggedIn && tripReferences && tripReference === undefined) {
      if (!tripReferences.some(x => x.tripHash === hash) && shared) {
        // console.log("useTripHashParams: Fetch for loggedIn user, get shared trip (not own trip)");
        tripGetShared(hash);
      }
    }
  }, [hash, isLoggedIn, shared, tripReference, tripReferences, tripGetShared]);

  /**
   * Fetch for guest user, get shared trip
   * TripReference
   */
  useEffect(() => {
    if (!isLoggedIn && shared && tripReference === undefined) {
      // console.log("useTripHashParams: Fetch for guest user, get shared trip");
      tripGetShared(hash);
    }
  }, [hash, isLoggedIn, shared, tripReference, tripReferences, tripGetShared]);

  /**
   * Login with hash for guest user
   */
  useEffect(() => {
    if (!isLoggedIn && hashLogin && window.tconfig.LOGIN_WITH_HASH) {
      // console.log("useTripHashParams: Login with hash ");
      navigate(`${LOGIN_WITH_HASH.PATH}/${hashParam}`, { replace: true });
    }
  }, [hashLogin, hashParam, navigate, isLoggedIn]);

  return {
    hash,
    hashLogin,
    shared,
    tripReference,
  };
};

export default useTripHashParams;
